import type { FC } from 'react'
import { useState } from 'react'

import { Popup } from '@skytech/ui'

import { usePolicyId } from '@/components/ecom-policy/hooks/use-policy-id'
import { useSelectProduct } from '@/components/ecom-policy/hooks/use-select-product'
import type { ProductGroupData } from '@/components/ecom-policy/types'
import { useTranslation } from '@/components/i18n/hooks'
import { useCart } from '@/contexts/cart/cart-context'

import { ProductCardWithInfo } from '../common/product-card/ProductCardWithInfo'

import { MultipleProductGroup } from './MultipleProductGroup'

import { usePolicyContext } from '../../Policy.context'
type ProductData = Omit<ProductGroupData, 'category' | 'groupKey' | 'isRecommended'>

const PolicyProductGroup: FC<
  ProductData & {
    allocation: Record<number, { covered: number; userPrice: number }>
    coverLeft: number
    isSelectionDisabled: boolean
    searchQuery: string
    isProductPriceVisible: boolean
  }
> = ({
  groupedProducts,
  imageUrl,
  description,
  priceFrom,
  groupName,
  inStock,
  isAccessory,
  isMandatory,
  assetType,
  allocation,
  coverLeft,
  isSelectionDisabled,
  searchQuery,
  isProductPriceVisible,
}) => {
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false)
  const translate = useTranslation()
  const policyId = usePolicyId()
  const policyCtx = usePolicyContext()
  const { getItemInCart, incrementQty, decrementQty, removeItem, setQty, qtyOf } = useCart()
  const { handleProductSelect, handleAccessorySelect } = useSelectProduct()

  const [selectedProductIds, setSelectedProductIds] = policyCtx.selectedProductIds
  const [selectedAccessoriesIds, setSelectedAccessoriesIds] = policyCtx.selectedAccessoriesIds
  const [productQuantityById, setProductQuantityById] = policyCtx.productQuantityById
  const [grossSalaryTaxBracket] = policyCtx.grossSalaryTaxBracket

  const productIds = groupedProducts.map(({ id }) => id)
  const selectedProductId = selectedProductIds?.find(id => productIds.includes(id))
  const selectedProduct = groupedProducts.find(({ id }) => id === selectedProductId)
  const selectedAccessoryId = selectedAccessoriesIds?.find(id => productIds?.includes(id))
  const selectedAccessory = groupedProducts.find(({ id }) => id === selectedAccessoryId)
  const selectedItem = selectedProduct || selectedAccessory
  const isProductSelected = Boolean(selectedProductId)
  const isAccessorySelected = Boolean(selectedAccessoryId)
  const isSelected = isAccessory ? isAccessorySelected : isProductSelected
  const isSingleProductGroup = groupedProducts.filter(product => !product.isMandatory).length === 1
  // Mandatory products are not selectable
  const productGroupPrice = isProductSelected ? selectedProduct?.price : priceFrom
  const price = isAccessory ? priceFrom : productGroupPrice || 0

  const isSelectedOnAnotherPolicy =
    !isSelectionDisabled &&
    Boolean(
      groupedProducts.some(product => {
        const item = getItemInCart(product.id)
        return (
          item &&
          !item.isGreenpoolProduct &&
          Number(item.policyId) !== policyId &&
          !item.product?.isMandatory
        )
      })
    )

  const userPrice =
    isSelected && !isSelectedOnAnotherPolicy && selectedItem && allocation[selectedItem.id]
      ? allocation[selectedItem.id]?.userPrice
      : Math.max(0, price - coverLeft)

  const isFromPriceTextVisible = isAccessory
    ? Boolean(
        !isAccessorySelected || (selectedAccessoriesIds && selectedAccessoriesIds?.length > 1)
      )
    : !isSelected

  // Products marked mandatory should not be visible in product selection
  if (isMandatory) return null

  const handleDeselect = () => {
    if (isSelectionDisabled) return
    if (!selectedProduct && !selectedAccessory) throw Error('No selected product to deselect')
    if (selectedAccessoriesIds && isAccessory && selectedAccessory) {
      const filteredSelectedAccessoriesIds = selectedAccessoriesIds?.filter(
        selectedAccessoryIdItem => selectedAccessoryIdItem !== selectedAccessory.id
      )
      setSelectedAccessoriesIds(filteredSelectedAccessoriesIds)
      setProductQuantityById({ ...productQuantityById, [selectedAccessory.id]: 0 })

      // Remove from cart
      removeItem(policyId, selectedAccessory.id)
      setQty(policyId, selectedAccessory.id, 0)
      return
    }

    if (!selectedProductIds || !selectedProduct) return
    const filteredSelectedProductIds = selectedProductIds?.filter(
      selectedProductIdItem => selectedProductIdItem !== selectedProduct.id
    )
    setSelectedProductIds(filteredSelectedProductIds)
    setProductQuantityById({ ...productQuantityById, [selectedProduct.id]: 0 })

    // Remove from cart
    removeItem(policyId, selectedProduct.id)
    setQty(policyId, selectedProduct.id, 0)
  }

  const isQuantitySelectionAvailable = Boolean(
    isSelected && isSingleProductGroup && selectedItem && selectedItem?.quantityLimit > 1
  )
  const quantity = selectedItem ? qtyOf(policyId, selectedItem.id) : 1
  const isQuantityLimitReached = selectedItem ? quantity + 1 > selectedItem?.quantityLimit : false

  const onCardSelect = () => {
    if (isSingleProductGroup) {
      if (isSelectionDisabled) return
      const [product] = groupedProducts
      if (product.isAccessory) {
        handleAccessorySelect({ product })
      } else {
        handleProductSelect({ product })
      }
    } else {
      setIsProductsModalOpen(!isProductsModalOpen)
    }
  }

  const onQuantityChange = (newQuantity: number, isIncrement: boolean) => {
    if (isSelectionDisabled || !selectedItem) return
    setProductQuantityById({
      ...productQuantityById,
      [selectedItem.id]: newQuantity,
    })
    if (isIncrement) {
      incrementQty(policyId, selectedItem.id)
    } else {
      decrementQty(policyId, selectedItem.id)
    }
  }

  return (
    <li className="m-0 p-0">
      <ProductCardWithInfo
        isSingle={isSingleProductGroup}
        isSelected={isSelected}
        productTitle={{ productName: groupName, productQuerySearch: searchQuery }}
        productImg={{ src: imageUrl || '', assetType: assetType || 'OTHER' }}
        productPrice={{
          employeePrice: isSelectionDisabled ? Math.max(0, price) : Math.max(0, userPrice),
          productPrice: Math.max(0, price),
          isFromPriceTextVisible,
          grossSalaryTaxBracket,
          isProductPriceVisible,
        }}
        productInStock={inStock}
        productQuantity={{
          isQuantitySelectionAvailable,
          quantity,
          isQuantityPlusDisabled: isQuantityLimitReached,
          onPlusClick: e => {
            e.stopPropagation()
            onQuantityChange(quantity + 1, true)
          },
          onMinusClick: e => {
            e.stopPropagation()
            if (!selectedItem) return
            if (quantity === 1) {
              handleDeselect()
            }
            onQuantityChange(quantity - 1, false)
          },
        }}
        onCardSelect={onCardSelect}
        isSelectedOnAnotherPolicy={isSelectedOnAnotherPolicy}
        isSelectionDisabled={isSelectionDisabled}
        product={groupedProducts[0]}
      />
      {!isSingleProductGroup && (
        <Popup
          variant="wide"
          isOpen={isProductsModalOpen}
          ariaLabel={translate('Modal with products')}
          onClose={() => setIsProductsModalOpen(false)}
        >
          <div>
            <MultipleProductGroup
              searchQuery={searchQuery}
              isAccessory={isAccessory}
              selectedGroupProductId={selectedProductId}
              groupName={groupName}
              groupedProducts={groupedProducts}
              description={description}
              allocation={allocation}
              coverLeft={coverLeft}
              isSelectionDisabled={isSelectionDisabled}
              isProductPriceVisible={isProductPriceVisible}
              onModalClose={() => setIsProductsModalOpen(false)}
            />
          </div>
        </Popup>
      )}
    </li>
  )
}

export default PolicyProductGroup
